import { useCallback, useState } from "react"

/**
 * The part of an RTK Query hook result that says whether what it holds answers the question asked.
 *
 * `data` is the last result the hook saw for any arguments, so on stepping to a new day it is still
 * the old day's appointments until the new ones land. `currentData` is only ever the answer for the
 * arguments asked now, and undefined until there is one — which is the only one the grid may paint.
 */
export interface QueryReadState {
  isFetching: boolean
  currentData?: unknown
}

/**
 * A read is in flight for what is on screen and nothing has arrived for it yet.
 *
 * False for a refetch of data already held — an invalidation after this app's own write, say — so
 * that the grid stays up while it is replaced rather than flickering to placeholders under an open
 * dialog. A refresh the user asked for is tracked separately; see `useTrackedRefresh`.
 */
export function awaitingCurrent(query: QueryReadState): boolean {
  return query.isFetching && query.currentData === undefined
}

/**
 * A refresh the user asked for, remembered against the view it was asked on.
 *
 * Refetching keeps `currentData` in place until the answer arrives, which is right for a refetch
 * nobody asked for and wrong for one somebody did: having pressed refresh, they should not be left
 * reading the grid they asked to replace as though it were the answer. So the press is tracked here,
 * until every read it started has settled.
 *
 * Keyed by view rather than held as a flag, so stepping to another day mid-refresh does not carry the
 * placeholders along with it — and stepping back to the day still refreshing picks them back up.
 *
 * @param viewKey What is on screen — the team and day, or the person and week.
 */
export function useTrackedRefresh(viewKey: string) {
  const [refreshingKey, setRefreshingKey] = useState<string | null>(null)

  const track = useCallback((reads: readonly PromiseLike<unknown>[]) => {
    setRefreshingKey(viewKey)
    void Promise.allSettled(reads).then(() =>
      setRefreshingKey(current => (current === viewKey ? null : current)))
  }, [viewKey])

  return { refreshing: refreshingKey === viewKey, track }
}
